function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-use-before-define */

import clsx from 'clsx';
import { produce } from 'immer';
import omit from 'lodash/omit';
import React, { useState } from 'react';
import { FormulaMetric, Metric, MetricsRequest } from 'app_api/projects/metrics/types';
import { ButtonReact } from 'common/components/button';
import { ButtonSize, ButtonTheme, ButtonType } from 'common/components/button/constants';
import { InputReact } from 'common/components/input';
import { InputSize, InputType, ValidationStatus } from 'common/components/input/types';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { SkeletonListReact } from 'common/components/skeleton-list';
import { SkeletonListRowSize } from 'common/components/skeleton-list/types';
import { DisplaySize, Placement } from 'common/components/tooltip/types';
import { generateCountCalculationMenuOptions } from 'common/query-builder/query-block/count-menu/util';
import { RowSelector } from 'common/query-builder/query-entry-section/components/selector';
import { Action, Calculation, EntryPart, EntrySectionUpdates, EntryType, FormulaItem, MetricEntry, QueryEventEntry, QueryFormulaEntry, QueryMetricEntry, RowLabelType, RowTypeComplexOption, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import { getQueryEntryUpdateFromCalculation, getSelectedCalculation, getSelectedCalculationLabel } from 'common/query-builder/query-entry-section/util';
// eslint-disable-next-line import/no-cycle
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useTranslation } from 'common/i18n/hooks';
import { CallingLocation } from 'common/query-builder/chart-customization/tracking';
import { MenuType, MultiMetricsSectionItem } from 'common/query-builder/query-block/multi-metrics-menu/types';
import { getMultiAttributionFromEntry } from 'common/query-builder/query-builder/util';
import { QueryEntrySectionReact } from 'common/query-builder/query-entry-section';
import { MultiAttributionAggregator } from 'common/query-builder/query-entry-section/components/aggregations';
import { EditControls } from 'common/query-builder/query-entry-section/components/edit-controls';
import { ControlBarAction, ControlBarActionItems, ControlBarActionValue } from 'common/query-builder/query-entry-section/constants';
import { QueryEntrySectionTrackingEvent } from 'common/query-builder/query-entry-section/tracking';
import { CountUsersOnceState, FeatureGate, ResourceType } from 'common/report/constants';
import { replaceFormulaReferences } from 'common/report/insights/contexts/formula-utils';
import { FormulaShowClause } from 'common/report/insights/util/types';
import { showMetricsApiErrorToast } from 'common/report/queries/app-api/metrics';
import { getProjectId } from 'common/report/util';
import { buildCreateEntityModalAttrs, getEditCapability } from 'common/report/util/saved-entities';
import { EntityType, SharableEntityType } from 'common/report/util/saved-entities/types';
import { MetricType, PRIMITIVE_MATH_TYPES, TopLevelMetricType } from 'common/types/reports/bookmark';
import { KEY_VALUES } from 'common/util/dom';
import { isModifiedShowClauseFormula } from 'common/util/formulas';
import { ModelStatus } from 'common/util/model-resource/model-resource';
import { createEntityModalPromise } from 'common/widgets/create-entity-modal';
import { promptInProductUpsellModal } from 'common/widgets/in-product-upsell-modal/prompt';
import { getUpsellInfo, ModalTrigger } from 'common/widgets/in-product-upsell-modal/util';
import { promptSharing } from 'common/widgets/sharing';
import { MetricUnitsButton } from 'iron/common/query-builder/chart-customization/metric-units';
import { BaseEntryRow } from '../base';
import { setEntryVisibility, toggleExpandedRow } from '../row-util';
import type { EntryRowProps } from '../types';
import { canSaveForPlan, getDefinition, getFormulaEntryName, getFormulaShowClauseTrackingProps, getFormulaValidationError, getMetricUpdateFromFormulaShowClause, getModel, getReferencedEntries, getReferencedMetricsFromEntries, getResolvedFormulaShowClause, getSaveDisabledMessage, getUpdatedFormulaAndMetrics, getUpdatedFormulaEntry, isUnsavedFormula } from './utils';
// eslint-disable-next-line import/no-cycle
import { helpers } from './helpers';
export function FormulaEntryRow(props: EntryRowProps<QueryFormulaEntry>) {
  var _getResolvedFormulaSh;
  const [entryEl, setEntryEl] = useState<HTMLDivElement>(null);
  const currentProject = useCurrentProject();
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const i18n = useTranslation();
  const {
    t,
    print
  } = i18n;
  const onControlSelect = (value: ControlBarActionValue) => {
    switch (value.action) {
      case ControlBarAction.Save:
        saveFormula();
        break;
      case ControlBarAction.RollingAverage:
      case ControlBarAction.Cumulative:
        handleCalculationSelected(value.calculation);
        break;
      case ControlBarAction.Show:
      case ControlBarAction.Hide:
        setEntryVisibility(props, value.action === ControlBarAction.Show);
        break;
      default:
        props.onControlSelect(value);
    }
  };

  /** @todo pablo -- simplify this code by remove usage of formula show clause formats here */
  const getFormulaShowClauseFromEntry = (entry: QueryFormulaEntry): FormulaShowClause => {
    var _entry$event, _entry$event2, _entry$event3, _entry$event4;
    return {
      // formula: entry.event,
      id: (_entry$event = entry.event) === null || _entry$event === void 0 ? void 0 : _entry$event.id,
      definition: (_entry$event2 = entry.event) === null || _entry$event2 === void 0 ? void 0 : _entry$event2.definition,
      referencedMetrics: (_entry$event3 = entry.event) === null || _entry$event3 === void 0 ? void 0 : _entry$event3.referencedMetrics,
      name: (_entry$event4 = entry.event) === null || _entry$event4 === void 0 ? void 0 : _entry$event4.name,
      type: TopLevelMetricType.Formula,
      isExpanded: entry.isExpanded,
      isHidden: entry.isHidden,
      display: entry.display,
      measurement: {
        cumulative: entry.cumulative,
        rolling: entry.rolling
      }
    };
  };
  const getFormulaError = () => {
    var _props$entry$event;
    return getFormulaValidationError(props, metrics, behaviors) || ((_props$entry$event = props.entry.event) === null || _props$entry$event === void 0 ? void 0 : _props$entry$event.error);
  };
  const isSelectingFormula = () => {
    return props.entry.event === null;
  };
  const isSavedFormula = () => {
    return isUnsavedFormula(props.entry) === false;
  };

  /**
   * Returns true when a saved formula has been edited
   */
  const isModifiedFormula = () => {
    return isModifiedShowClauseFormula(props.entry.event);
  };
  const isSaveEnabled = () => {
    return !(helpers.isPlaceholder(props) || getSaveDisabledMessage({
      props,
      metrics,
      behaviors,
      i18n,
      currentProject: currentProject.get().value
    }));
  };
  const handleUpsertedFormula = (_ref: {
    metric: Metric;
    isCreating?: boolean;
    isDuplicating?: boolean;
  }) => {
    let {
      metric,
      isCreating,
      isDuplicating
    }: {
      metric: Metric;
      isCreating?: boolean;
      isDuplicating?: boolean;
    } = _ref;
    if (!metric) {
      return;
    }
    const entry: QueryFormulaEntry = {
      type: ResourceType.Formula,
      display: metric.definition.display,
      event: {
        id: metric.id
      },
      isExpanded: false,
      multiAttribution: props.entry.multiAttribution
    };
    if (isCreating) {
      track(QueryEntrySectionTrackingEvent.SaveFormula, _objectSpread(_objectSpread({}, getTrackingProps(entry)), {}, {
        'Save as New': isDuplicating
      }));
    } else {
      track(QueryEntrySectionTrackingEvent.SaveFormulaChanges, getTrackingProps(entry));
    }
    props.updateEntry(Action.Edit, entry, {
      trackingProps: getTrackingProps(entry)
    });
  };
  const saveFormula = async function saveFormula() {
    var _currentProject$get;
    let isDuplicating = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (isSavedFormula() && isDuplicating === false) {
      return handleSaveModifications();
    }
    let {
      name,
      definition,
      referencedMetrics
    } = props.entry.event;

    /** when saving an inline formula, we take reference metrics from the main report */
    if (isModifiedFormula() === false) {
      const updatedDefinition = getUpdatedFormulaAndMetrics({
        name,
        definition
      }, props.getEntries() as Array<QueryMetricEntry | MetricEntry>);
      referencedMetrics = updatedDefinition.referencedMetrics;
      definition = updatedDefinition.definition;
    }
    const metricEntity: MetricsRequest = {
      type: "formula",
      name,
      description: "",
      definition: {
        formula: {
          definition,
          referencedMetrics
        },
        display: props.entry.display,
        measurement: getResolvedFormulaShowClause({
          formulaEntry: props.entry,
          metrics,
          behaviors
        }).measurement
      }
    };
    const result = await createEntityModalPromise(buildCreateEntityModalAttrs({
      projectId: parseInt(getProjectId(), 10),
      entityType: EntityType.Metric,
      modalOpen: true,
      isEditing: false,
      isDuplicating,
      entity: metricEntity,
      showShareManagement: (_currentProject$get = currentProject.get()) === null || _currentProject$get === void 0 || (_currentProject$get = _currentProject$get.value) === null || _currentProject$get === void 0 ? void 0 : _currentProject$get.getFeatureGate(FeatureGate.Sharing),
      i18n
    }), {
      existingEntityNames: metrics.getAllValues().map(value => value.name)
    });
    if (result.didSave) {
      let metric: Metric;
      try {
        const results = await metrics.create(_objectSpread(_objectSpread({}, metricEntity), {}, {
          name: result.name,
          description: result.description
        }));
        metric = Object.values(results)[0];
      } catch (_unused) {
        showMetricsApiErrorToast();
      }
      handleUpsertedFormula({
        metric,
        isCreating: true,
        isDuplicating
      });
      if (result.didShare) {
        await promptSharing({
          type: SharableEntityType.Metric,
          id: metric.id,
          'project-id': Number(getProjectId())
        });
      }
    }
  };

  /**
   * Turns this entry into an empty inline entry.
   */
  const handleCreateInline = (menuType: MenuType) => {
    props.onEntryRowCreate({
      type: SelectorTypeDefinitions.Metrics,
      event: null,
      filters: []
    }, menuType);
  };
  const onDefinitionCommit = (newDefinition: string) => {
    if (newDefinition) {
      updateFormula({
        definition: newDefinition
      });
      props.setActivePart(EntryPart.Value, "close");
    } else if (!props.isStaged && props.sectionProps.queryOrigin !== "canvas") {
      // remove the formula entry if its definition is edited to be the empty string.
      // the !isStagedEntry check is to limit this codepath to editing existing definition rather than newly
      // staged formula.
      props.removeEntry();
      props.setActivePart(EntryPart.Value, "close");
    }
  };
  const handleSelectMultiMetrics = (sectionItem: MultiMetricsSectionItem) => {
    props.onEntryRowSelect({
      type: SelectorTypeDefinitions.Metrics,
      event: null,
      filters: []
    }, props.entry, sectionItem, props.index);
  };
  const handleDefinitionKeydown = (ev: KeyboardEvent) => {
    if (ev.key === KEY_VALUES.ENTER) {
      ev.preventDefault();
      const inputEl = ev.currentTarget as HTMLInputElement;
      inputEl.blur();
    }
  };
  const handleDefinitionInput = (ev: InputEvent) => {
    const inputEl = ev.currentTarget as HTMLInputElement;
    inputEl.value = inputEl.value.toUpperCase();
  };
  const handleDefinitionBlur = (ev: FocusEvent) => {
    onDefinitionCommit((ev.currentTarget as HTMLInputElement).value);
  };
  const handleDefinitionFocus = () => {
    if (isSavedFormula()) {
      return;
    }
    if (props.activePart === EntryPart.Value) {
      // The formula will already be openEntry if it was added as new formula and autofocused. In this
      // case, don't update to avoid an unnecessary re-render
      return;
    }
    props.setActivePart(EntryPart.Value, "open");
  };
  const handleCalculationRemoved = () => {
    updateFormula({
      rolling: null,
      cumulative: null
    });
  };
  const handleCalculationSelected = (calculation: Calculation) => {
    updateFormula(getQueryEntryUpdateFromCalculation({
      originalCalculation: props.entry,
      calculation
    }));
  };
  const onReferencedMetricsUpdate = (updates: EntrySectionUpdates<QueryEventEntry | MetricEntry>) => {
    const {
      entries,
      formulaRefUpdates,
      requery
    } = updates;
    // if not requerying, no need to update referenced metrics
    if (!requery) {
      return;
    }
    let definition = getDefinition(props.entry, metrics, behaviors);

    // update formula references
    if (formulaRefUpdates) {
      definition = replaceFormulaReferences(definition, formulaRefUpdates);
    }
    updateFormula({
      referencedMetrics: getReferencedMetricsFromEntries(entries),
      definition
    });
  };
  const updateFormula = (modifiedParams: Partial<Pick<FormulaItem, `name` | `definition` | `referencedMetrics`> & Pick<QueryFormulaEntry, `rolling` | `cumulative` | `display`>>, requery?: boolean) => {
    const entry = getUpdatedFormulaEntry(props.entry, modifiedParams, metrics, behaviors);
    props.updateEntry(Action.Edit, entry, {
      trackingProps: props.sectionProps.queryOrigin === "canvas" ? {
        'query origin': "canvas"
      } : _objectSpread({}, getTrackingProps(entry)),
      requery: requery !== null && requery !== void 0 ? requery : true
    });
  };
  const handleResetModifications = () => {
    const savedFormula = getModel(props.entry, metrics).value as FormulaMetric;
    track(QueryEntrySectionTrackingEvent.ResetFormula, getTrackingProps(props.entry));

    // NOTE: we need to have this logic here, rather than higher-up with all the other multi-attribution
    // compatibility checks because if the formula is reset to be unique without count user once, one of the
    // higher-up compatibility checks would re-add count users once to this formula (the aforementioned
    // compatibility check adds count users once to metrics with multi-attribution and count type unique).
    let multiAttribution = props.entry.multiAttribution;
    if (multiAttribution &&
    // remove multi-attribution if the reset formula has count type unique without count user once
    savedFormula.definition.formula.referencedMetrics.some(c => c.measurement.math === PRIMITIVE_MATH_TYPES.UNIQUE && !c.measurement.segmentMethod)) {
      multiAttribution = null;
    }
    props.updateEntry(Action.Edit, produce(props.entry, draft => {
      const updates: Partial<QueryFormulaEntry> = {
        event: {
          // removing all fields except id will make this an unmodified saved formula
          id: savedFormula.id
        },
        rolling: null,
        cumulative: null,
        display: null,
        multiAttribution
      };
      Object.assign(draft, updates);
    }));
  };
  const handleSaveModifications = async function handleSaveModifications() {
    let saveAsNew = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (saveAsNew) {
      return saveFormula(saveAsNew);
    }
    let metric: Metric;
    try {
      const results = await metrics.patch(getModel(props.entry, metrics).value.id, getMetricUpdateFromFormulaShowClause(getResolvedFormulaShowClause({
        formulaEntry: props.entry,
        metrics,
        behaviors,
        hydrateSavedBehaviors: false
      })));
      metric = Object.values(results)[0];
    } catch (_unused2) {
      showMetricsApiErrorToast();
    }
    handleUpsertedFormula({
      metric
    });
  };
  const getReferencedMetricsRowTypes = () => {
    const types = props.sectionProps.rowTypes;
    return types.filter(option => typeof option === "string" && option !== ResourceType.Formula || (option as RowTypeComplexOption).value !== ResourceType.Formula);
  };
  const getTrackingProps = (entry: QueryFormulaEntry) => {
    return getFormulaShowClauseTrackingProps({
      behaviorsResource: behaviors,
      formulaShowClause: getFormulaShowClauseFromEntry(entry),
      i18n,
      metricsResource: metrics
    });
  };
  const track = function track(event: QueryEntrySectionTrackingEvent) {
    let extraProps: Obj = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    props.dispatchTrack(event, _objectSpread(_objectSpread({}, props.getEntryTrackingProps()), extraProps));
  };
  const calculation = getSelectedCalculation(((_getResolvedFormulaSh = getResolvedFormulaShowClause({
    formulaEntry: props.entry,
    metrics,
    behaviors
  })) === null || _getResolvedFormulaSh === void 0 ? void 0 : _getResolvedFormulaSh.measurement) || {});
  const {
    isExpanded
  } = props.entry;
  const isCollapsedSavedFormula = isSavedFormula() && !isExpanded;
  const formulaError = getFormulaError();
  const model = getModel(props.entry, metrics);
  const isLoadingModel = (model === null || model === void 0 ? void 0 : model.status) === ModelStatus.Loading;
  const isPlaceholder = helpers.isPlaceholder(props);
  const saveDisabledMessage = getSaveDisabledMessage({
    i18n,
    props,
    metrics,
    behaviors,
    currentProject: currentProject.get().value
  });

  // present the formula selector when first adding a row and saving is enabled
  // and when a saved formula is selected
  const selectorRenderer = () => {
    return isSelectingFormula() === false && isUnsavedFormula(props.entry) ? <div className='mp-query-entry-selector formula-name' data-sentry-component="selectorRenderer" data-sentry-source-file="index.tsx">{getFormulaEntryName(props.entry, metrics, behaviors)}</div> : <div className={clsx("mp-query-entry-selector", "formula-name", {
      'is-saved': isSavedFormula() && !isModifiedFormula()
    })} data-sentry-component="selectorRenderer" data-sentry-source-file="index.tsx">
        <RowSelector {...props} contextPanelParentElement={entryEl} isPlaceholder={isPlaceholder} selected={{
        menuType: MenuType.Formula,
        value: props.entry.event
      }} selectorType={SelectorTypeDefinitions.Metrics} onSelect={(sectionItem: MultiMetricsSectionItem) => handleSelectMultiMetrics(sectionItem)} onCreate={(ev: {
        detail: {
          item: MenuType;
        };
      }) => handleCreateInline(ev.detail.item)} data-sentry-element="RowSelector" data-sentry-source-file="index.tsx" />
        {isCollapsedSavedFormula && <MultiAttributionAggregator {...props} />}
      </div>;
  };
  const renderReferencedEntries = () => {
    const referencedEntries = getReferencedEntries(props.entry, metrics, behaviors);
    // NOTE: if referencedEntries is 0-length array, referenced entries section should still render
    if (!referencedEntries) {
      return null;
    }
    return <QueryEntrySectionReact elref='subEntry' className='sub-entry' {...props.nestedSectionParams} showDefaultMetricSelector={false} enableEventIcon entryType={EntryType.Event} addEntryButtonLabel='Metric' addEntryButtonTooltipText='Add a metric which can be referenced in the formula' addEntryMenuWidth={130} showTopEvents={false} countUsersOnceState={getMultiAttributionFromEntry(props.entry) ? CountUsersOnceState.Required : props.sectionProps.countUsersOnceState} dateRange={props.sectionProps.dateRange} dateUnit={props.sectionProps.dateUnit} isReferencedMetrics metricsMode rowTypes={getReferencedMetricsRowTypes()} excludedMetricsRowTypes={[MetricType.Formula]} rowLabelType={RowLabelType.Alpha} rowIndex={props.index} rowDraggable trackProperties={_objectSpread(_objectSpread({}, props.nestedSectionParams.trackProperties), {}, {
      sectionName: "referenced-metrics"
    })} entries={referencedEntries} onUpdate={onReferencedMetricsUpdate} data-sentry-element="QueryEntrySectionReact" data-sentry-component="renderReferencedEntries" data-sentry-source-file="index.tsx" />;
  };

  /** renders saving controls based on the user's permission */
  const renderEditControls = (formulaEntry: QueryFormulaEntry) => {
    var _props$entry$event2;
    if (isLoadingModel || !canSaveForPlan({
      currentProject: currentProject.get().value,
      metrics,
      entry: props.entry
    })) {
      if (getUpsellInfo(currentProject.get().value, ModalTrigger.FORMULA, i18n)) {
        return <ButtonReact theme={ButtonTheme.Secondary} icon={"brand-upgrade"} onClick={() => promptInProductUpsellModal(ModalTrigger.FORMULA)}>
            Save Formula
          </ButtonReact>;
      }
      return null;
    }
    const isSaving = metrics.isLoading((_props$entry$event2 = props.entry.event) === null || _props$entry$event2 === void 0 ? void 0 : _props$entry$event2.id);
    const isSaved = isSavedFormula();
    const canSave = isSaved ? isSaveEnabled() && isModifiedFormula() : true;
    const canEdit = isSaved ? getEditCapability(model.value).enableEdit : false;
    const resolvedFormula = getResolvedFormulaShowClause({
      formulaEntry,
      metrics,
      behaviors
    });
    const metricUnitsButton = <MetricUnitsButton insideFooter callingLocation={CallingLocation.Metric} chartType={props.currentChartType} setEntry={entry => updateFormula(omit(entry, "type"), false)} showDefaultMetricUnitSelector={props.showDefaultMetricUnitSelector} entry={resolvedFormula} />;
    return <>
        <EditControls resetTooltip='Reverts the formula to its previously saved state' isSaving={isSaving} isSaved={isSaved} canSave={canSave} canEdit={canEdit} metricUnitsSelector={metricUnitsButton} canReset={isModifiedFormula()} saveDisabledMessage={saveDisabledMessage} onReset={handleResetModifications} entityType={t("query-builder.metrics.formula")} onSave={() => handleSaveModifications(false)} onClone={() => handleSaveModifications(true)} data-sentry-element="EditControls" data-sentry-source-file="index.tsx" />
      </>;
  };
  const suffixRenderer = isSelectingFormula() || isCollapsedSavedFormula ? null : () => {
    if (isLoadingModel) {
      return <div className='mp-query-entry-row for-formula-definition'>
                <SkeletonListReact numRows={4} rowSize={SkeletonListRowSize.Large} className='mp-formulas-menu-skeleton-list' />
              </div>;
    }
    return <div className='mp-query-entry-row for-formula-definition'>
              <InputReact className='formula-definition' elref='formulaDefinition' type={InputType.Text} size={InputSize.Medium} placeholder='eg. (A+B)/(A-B) * 100' autoFocus={props.activePart === EntryPart.Value} {...formulaError && {
        validationStatus: ValidationStatus.Invalid,
        errorMessage: formulaError
      }} value={getDefinition(props.entry, metrics, behaviors)} onKeydown={handleDefinitionKeydown} onInput={handleDefinitionInput} onBlur={handleDefinitionBlur} onFocus={handleDefinitionFocus} />
              {renderReferencedEntries()}
              {calculation.calculationType && <div className='calculation-selector'>
                  <SelectReact key='calculation-selector' elref='calculationSelector' label={print(getSelectedCalculationLabel(calculation, props.sectionProps.dateUnit))} disableSearch menuWidth={180} trackName={props.sectionProps.trackName} theme={SelectTheme.Tertiary} disableTooltip onChange={(ev: CustomEvent) => handleCalculationSelected(ev.detail.item.value)} onMenuChange={ev => props.onSelectMenuChange(ev, EntryPart.Calculation)} sections={generateCountCalculationMenuOptions({
          selectedPlotStyle: props.sectionProps.plotStyle,
          selectedCalculation: calculation,
          selectedDateUnit: props.sectionProps.dateUnit
        })} />
                  <ButtonReact className='remove-button' theme={ButtonTheme.Tertiary} size={ButtonSize.Inline} type={ButtonType.Destructive} tooltip={{
          placement: Placement.RightCenter,
          size: DisplaySize.Small,
          text: "Remove"
        }} icon='x' onClick={handleCalculationRemoved} />
                </div>}

              {renderEditControls(props.entry)}

              <MultiAttributionAggregator {...props} />
            </div>;
  };
  const actionsRenderer = () => {
    if (props.disableCollapse || isUnsavedFormula(props.entry) ||
    // hide the expand button when a saved formula is modified so to that modifications are always visible and
    // prompts the user there are underlying changes to a saved formula being applied
    isModifiedFormula() &&
    // Although it isn't expected to happen, don't hide button when it's collapsed to prevent possibility of
    // collapsed row that's impossible to expand
    isExpanded &&
    // This is to allow formula to be collapsible when "count users once" is auto-added to referenced metrics
    // when using attribution. Since "count users once" isn't savable, we still allow it to be collapsible via
    // this check
    !saveDisabledMessage) {
      return null;
    }
    return <ButtonReact className='expand-control' icon={isExpanded ? "collapse-diagonal" : "expand"} theme={ButtonTheme.Tertiary} size={ButtonSize.Medium} tooltip={{
      text: isExpanded ? t("query-builder.actions.collapse") : t("query-builder.actions.expand")
    }} onClick={() => toggleExpandedRow(props, getTrackingProps(props.entry))} data-sentry-element="ButtonReact" data-sentry-component="actionsRenderer" data-sentry-source-file="index.tsx" />;
  };
  return <>
      <BaseEntryRow ref={setEntryEl} {...props} showDefaultMetricUnitSelector={false} isPlaceholder={false} renderActions={actionsRenderer} renderSelector={selectorRenderer} renderSuffix={suffixRenderer} onControlSelect={onControlSelect} overflowControls={[...props.getCommonOverflowControls(),
    // display the save action for inline formulas or
    // for saved formulas with modifications

    !isPlaceholder && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Save]), saveDisabledMessage && {
      tooltip: {
        text: saveDisabledMessage,
        size: DisplaySize.Medium
      },
      isDisabled: true
    }),
    // hide action only for insights
    props.sectionProps.enableHideEntry && !isPlaceholder && ControlBarActionItems[props.entry.isHidden ? ControlBarAction.Show : ControlBarAction.Hide],
    // calculation options
    ControlBarActionItems[ControlBarAction.RollingAverage]({
      currentRollingWindowSize: props.entry.rolling,
      dateUnit: props.sectionProps.dateUnit
    }), ControlBarActionItems[ControlBarAction.Cumulative]]} data-sentry-element="BaseEntryRow" data-sentry-source-file="index.tsx" />
    </>;
}